import { useState } from "react";
import adminFetch from "../../utils/adminFetch";
import { getTokenPayload } from "../ProtectedRoute";

function timeAgo(str) {
  if (!str) return "";
  const diff = Date.now() - new Date(str).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1)  return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)  return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7)  return `${days}d ago`;
  return new Date(str).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function formatDate(str) {
  if (!str) return "";
  return new Date(str).toLocaleString("en-US", {
    month: "short", day: "numeric", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

export default function ActivityNotes({ entityType, entityId, notes: initialNotes, isMock }) {
  const [notes,    setNotes]    = useState(initialNotes || []);
  const [text,     setText]     = useState("");
  const [saving,   setSaving]   = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [error,    setError]    = useState(null);
  const current = getTokenPayload();

  const addNote = async () => {
    if (!text.trim() || isMock) return;
    setSaving(true);
    setError(null);
    try {
      const res  = await adminFetch(
        `${process.env.REACT_APP_API_URL}/api/admin/${entityType}/${entityId}/notes`,
        { method: "POST", body: JSON.stringify({ text }) }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to save note.");
      setNotes(prev => [data.note, ...prev]);
      setText("");
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteNote = async (noteId) => {
    if (isMock) return;
    setDeleting(noteId);
    try {
      await adminFetch(
        `${process.env.REACT_APP_API_URL}/api/admin/${entityType}/${entityId}/notes/${noteId}`,
        { method: "DELETE" }
      );
      setNotes(prev => prev.filter(n => n._id !== noteId));
    } catch (_) {}
    setDeleting(null);
  };

  const canDelete = (note) =>
    note.authorId === current?.userId || current?.role === "super-admin";

  return (
    <div>
      <p style={{ fontFamily: "'Cinzel', serif", fontSize: "8px", letterSpacing: "0.22em", textTransform: "uppercase", color: "rgba(1,46,114,0.38)", marginBottom: "12px" }}>
        Activity Notes
      </p>

      {/* Add note input */}
      <div style={{ marginBottom: "16px" }}>
        <textarea
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) addNote(); }}
          placeholder={isMock ? "Notes disabled on demo data" : "Add a note… (⌘Enter to save)"}
          disabled={isMock || saving}
          rows={2}
          style={{
            width: "100%", fontFamily: "'Constantia', serif", fontSize: "13px", color: "#012E72",
            background: "#fff", border: "1px solid rgba(1,46,114,0.15)", borderRadius: "10px",
            padding: "10px 12px", outline: "none", resize: "vertical", boxSizing: "border-box",
            opacity: isMock ? 0.5 : 1,
          }}
          onFocus={e => { e.target.style.borderColor = "#002DB5"; e.target.style.boxShadow = "0 0 0 3px rgba(0,45,181,0.08)"; }}
          onBlur={e => { e.target.style.borderColor = "rgba(1,46,114,0.15)"; e.target.style.boxShadow = "none"; }}
        />
        {error && (
          <p style={{ fontFamily: "'Constantia', serif", fontSize: "12px", color: "#dc2626", marginTop: "4px" }}>{error}</p>
        )}
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "6px" }}>
          <button
            onClick={addNote}
            disabled={isMock || saving || !text.trim()}
            style={{
              fontFamily: "'Cinzel', serif", fontSize: "9px", letterSpacing: "0.14em",
              textTransform: "uppercase", color: "#fff", padding: "7px 16px",
              background: "linear-gradient(135deg,#012E72,#002DB5)",
              border: "none", borderRadius: "8px", cursor: "pointer",
              opacity: (!text.trim() || saving || isMock) ? 0.45 : 1, transition: "opacity 0.15s",
            }}
          >
            {saving ? "Saving…" : "Add Note"}
          </button>
        </div>
      </div>

      {/* Notes list */}
      {notes.length === 0 ? (
        <div style={{ textAlign: "center", padding: "20px 0" }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="rgba(1,46,114,0.15)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ margin: "0 auto 8px", display: "block" }}>
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
          <p style={{ fontFamily: "'Cinzel', serif", fontSize: "9px", letterSpacing: "0.16em", textTransform: "uppercase", color: "rgba(1,46,114,0.2)" }}>
            No notes yet
          </p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          {notes.map((note) => (
            <div key={note._id} style={{
              background: "#fff", borderRadius: "12px", padding: "12px 14px",
              border: "1px solid rgba(1,46,114,0.08)", position: "relative",
            }}>
              {/* Header row */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "6px", gap: "8px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  {/* Author avatar */}
                  <div style={{ width: "22px", height: "22px", borderRadius: "50%", flexShrink: 0, background: "linear-gradient(135deg,#012E72,#002DB5)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <span style={{ fontFamily: "Arial", fontSize: "9px", color: "#fff", fontWeight: 700 }}>
                      {(note.authorName || "?")[0].toUpperCase()}
                    </span>
                  </div>
                  <p style={{ fontFamily: "'Cinzel', serif", fontSize: "10px", letterSpacing: "0.1em", color: "#012E72", fontWeight: 700 }}>
                    {note.authorName}
                  </p>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <p style={{ fontFamily: "'Constantia', serif", fontSize: "11px", color: "#94a3b8" }} title={formatDate(note.createdAt)}>
                    {timeAgo(note.createdAt)}
                  </p>
                  {canDelete(note) && (
                    <button
                      onClick={() => deleteNote(note._id)}
                      disabled={deleting === note._id}
                      style={{ background: "none", border: "none", cursor: "pointer", color: "rgba(1,46,114,0.2)", padding: "2px", lineHeight: 1, transition: "color 0.15s" }}
                      onMouseEnter={e => e.currentTarget.style.color = "#dc2626"}
                      onMouseLeave={e => e.currentTarget.style.color = "rgba(1,46,114,0.2)"}
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/>
                      </svg>
                    </button>
                  )}
                </div>
              </div>

              {/* Note text */}
              <p style={{ fontFamily: "'Constantia', serif", fontSize: "13px", color: "#334155", lineHeight: 1.65, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {note.text}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
